// Normalized gameplay → audio event vocabulary. Components dispatch these minimal events
// (identifiers + safe state only — never narrative text, balances, emails, or tokens).
export const GameplayEvent = {
  ROUTE_ENTERED: "route_entered",
  EXPEDITION_SELECTED: "expedition_selected",
  CREW_CONFIRMED: "crew_confirmed",
  STAKE_REVIEW_OPENED: "stake_review_opened",
  STAKE_CONFIRMED: "stake_confirmed",
  SESSION_STARTED: "expedition_session_started",
  DECISION_PRESENTED: "decision_presented",
  DECISION_CONFIRMED: "decision_confirmed",
  DANGER_LEVEL_CHANGED: "danger_level_changed",
  SKILL_CHALLENGE_STARTED: "skill_challenge_started",
  SKILL_CHALLENGE_COMPLETED: "skill_challenge_completed",
  OUTCOME_CONFIRMED: "outcome_confirmed",
  CREW_INJURED: "crew_injured",
  RECOVERY_STARTED: "recovery_started",
  RECOVERY_COMPLETED: "recovery_completed",
  RANK_PROMOTED: "rank_promoted",
  ACHIEVEMENT_UNLOCKED: "achievement_unlocked",
  PRESTIGE_TIER_CHANGED: "prestige_tier_changed",
  RIVAL_OVERTAKE_CONFIRMED: "rival_overtake_confirmed",
  SEASON_FINALIZED: "season_finalized",
  CONNECTION_LOST: "connection_lost",
  CONNECTION_RESTORED: "connection_restored",
  // crew motifs (decorative, layered over the base soundtrack)
  CREW_SELECTED: "crew_selected",
  CREW_CONTRIBUTION_CONFIRMED: "crew_contribution_confirmed",
  CREW_LOYALTY_INCREASED: "crew_loyalty_increased",
  CREW_LOYALTY_DECREASED: "crew_loyalty_decreased",
  CREW_RECOVERED: "crew_recovered",
  CREW_DEFINING_MOMENT_CONFIRMED: "crew_defining_moment_confirmed",
  CREW_CENTRAL_TO_OUTCOME: "crew_central_to_outcome",
  CREW_LEVEL_INCREASED: "crew_level_increased",
  CREW_TRAIT_UNLOCKED: "crew_trait_unlocked",
};

// Crew audio events → motif role + priority. Priority shares the global order so a crew motif
// never interrupts a higher-priority safety/outcome/critical-danger state.
export const CREW_EVENTS = {
  crew_selected: { role: "base", priority: 200, oneTime: false },
  crew_contribution_confirmed: { role: "base", priority: 300, oneTime: true },
  crew_loyalty_increased: { role: "base", priority: 320, oneTime: true },
  crew_loyalty_decreased: { role: "injury", priority: 320, oneTime: true },
  crew_injured: { role: "injury", priority: 520, oneTime: true },
  crew_recovered: { role: "recovery", priority: 480, oneTime: true },
  crew_defining_moment_confirmed: { role: "defining", priority: 700, oneTime: true },
  crew_central_to_outcome: { role: "defining", priority: 620, oneTime: true },
  crew_level_increased: { role: "base", priority: 340, oneTime: true },
  crew_trait_unlocked: { role: "defining", priority: 360, oneTime: true },
};
export const CREW_EVENT_SET = new Set(Object.keys(CREW_EVENTS));

// Events that MUST originate from a confirmed server response (never client optimism).
export const SERVER_CONFIRMED = new Set([
  GameplayEvent.SESSION_STARTED, GameplayEvent.DECISION_CONFIRMED, GameplayEvent.DANGER_LEVEL_CHANGED,
  GameplayEvent.SKILL_CHALLENGE_STARTED, GameplayEvent.SKILL_CHALLENGE_COMPLETED, GameplayEvent.OUTCOME_CONFIRMED,
  GameplayEvent.CREW_INJURED, GameplayEvent.RECOVERY_STARTED, GameplayEvent.RECOVERY_COMPLETED,
  GameplayEvent.RANK_PROMOTED, GameplayEvent.ACHIEVEMENT_UNLOCKED, GameplayEvent.PRESTIGE_TIER_CHANGED,
  GameplayEvent.RIVAL_OVERTAKE_CONFIRMED, GameplayEvent.SEASON_FINALIZED,
  GameplayEvent.CREW_SELECTED, GameplayEvent.CREW_CONTRIBUTION_CONFIRMED,
  GameplayEvent.CREW_LOYALTY_INCREASED, GameplayEvent.CREW_LOYALTY_DECREASED,
  GameplayEvent.CREW_RECOVERED, GameplayEvent.CREW_DEFINING_MOMENT_CONFIRMED,
  GameplayEvent.CREW_CENTRAL_TO_OUTCOME, GameplayEvent.CREW_LEVEL_INCREASED,
  GameplayEvent.CREW_TRAIT_UNLOCKED,
]);

// One-time ceremonial/result cues (deduplicated). Rest are ambient/loop transitions.
export const ONE_TIME_CUES = new Set([
  GameplayEvent.OUTCOME_CONFIRMED, GameplayEvent.RANK_PROMOTED, GameplayEvent.ACHIEVEMENT_UNLOCKED,
  GameplayEvent.PRESTIGE_TIER_CHANGED, GameplayEvent.RIVAL_OVERTAKE_CONFIRMED, GameplayEvent.SEASON_FINALIZED,
]);

// Priority (higher wins when multiple one-time events arrive together).
export const EVENT_PRIORITY = {
  offline: 1000,
  [GameplayEvent.OUTCOME_CONFIRMED]: 900,
  [GameplayEvent.PRESTIGE_TIER_CHANGED]: 800,
  [GameplayEvent.RANK_PROMOTED]: 700,
  [GameplayEvent.ACHIEVEMENT_UNLOCKED]: 600,
  [GameplayEvent.RIVAL_OVERTAKE_CONFIRMED]: 500,
};
